import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-serif text-white mb-4">Ashbourne</h3>
            <p className="text-sm text-gray-400 leading-relaxed mb-4">
              A boutique international law firm delivering sophisticated legal solutions to corporations and high net worth individuals worldwide.
            </p>
            <div className="mb-4">
              <Link to="/social-responsibility" className="text-sm text-gray-400 hover:text-white transition-colors flex items-center">
                <span className="mr-2">❤️</span> 1% for Children's Futures
              </Link>
            </div>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Practice Areas */}
          <div>
            <h4 className="text-white font-medium mb-4">Practice Areas</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/practice-areas" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Corporate Law
                </Link>
              </li>
              <li>
                <Link to="/practice-areas" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Commercial Law
                </Link>
              </li>
              <li>
                <Link to="/practice-areas" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Cross-border M&A
                </Link>
              </li>
              <li>
                <Link to="/practice-areas" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Contract Law
                </Link>
              </li>
              <li>
                <Link to="/practice-areas" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Arbitration
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-medium mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm text-gray-400 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/sectors" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Industry Sectors
                </Link>
              </li>
              <li>
                <Link to="/case-studies" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Case Studies
                </Link>
              </li>
              <li>
                <Link to="/insights" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Insights
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/social-responsibility" className="text-sm text-gray-400 hover:text-white transition-colors">
                  Social Impact
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-gray-400 hover:text-white transition-colors">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-medium mb-4">London Global HQ</h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400">
                  3rd Floor, 45 Albemarle Street<br />
                  London W1S 4JL
                </span>
              </li>
              <li className="flex items-center">
                <Phone className="h-5 w-5 mr-2 flex-shrink-0" />
                <a href="tel:+442071234567" className="text-sm text-gray-400 hover:text-white transition-colors">
                  +44 20 7123 4567
                </a>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 mr-2 flex-shrink-0" />
                <a href="mailto:contact@ashbournelaw.co.uk" className="text-sm text-gray-400 hover:text-white transition-colors">
                  contact@ashbournelaw.co.uk
                </a>
              </li>
            </ul>
            
            {/* SRA Digital Badge - Below Email */}
            <div className="mt-6">
              <div style={{maxWidth: '180px', maxHeight: '106px', width: '100%'}}>
                <div style={{position: 'relative', paddingBottom: '59.1%', height: 0, overflow: 'hidden'}}>
                  <iframe 
                    frameBorder="0" 
                    scrolling="no" 
                    allowTransparency="true" 
                    src="https://cdn.yoshki.com/iframe/55845r.html" 
                    style={{
                      border: '0px', 
                      margin: '0px', 
                      padding: '0px', 
                      backgroundColor: 'transparent', 
                      top: '0px', 
                      left: '0px', 
                      width: '100%', 
                      height: '100%', 
                      position: 'absolute'
                    }}
                    title="SRA Digital Badge"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Text - Above Footer Bar */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            Authorised and regulated by the Solicitors Regulation Authority, SRA No. 8016302. A company registered in England & Wales, Company No. 12872698.
          </p>
        </div>

        <div className="mt-4 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Ashbourne Law. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 mt-4 md:mt-0">
              <Link to="/terms-of-engagement" className="text-sm text-gray-400 hover:text-white transition-colors">
                Terms of Engagement
              </Link>
              <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-sm text-gray-400 hover:text-white transition-colors">
                Cookie Policy
              </Link>
              <Link to="/code-of-conduct" className="text-sm text-gray-400 hover:text-white transition-colors">
                Code of Conduct
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
